import { Button, Card, EmptyState, Kbd, useToast } from '@efira/ui';
import { Link } from 'react-router';

import { usePlaySound, useSounds } from '../../api/queries';
import type { SoundRow } from '../../api/queries';

/**
 * Сетка падов: то, чем стример пользуется в эфире.
 *
 * ПАД - ЭТО КОМАНДА ОВЕРЛЕЮ. Звук звучит там, где его слышит эфир, а не в
 * браузере стримера: браузер консоли OBS не захватывает. Разница между «в
 * эфир» и «себе» названа словами - здесь подсказкой у сетки, в библиотеке
 * настроек подписью у кнопки прослушивания.
 *
 * ПУСТАЯ ЯЧЕЙКА НЕ РЕАГИРУЕТ НА НАЖАТИЕ, но и не молчит: заполнение идёт
 * только через библиотеку, и ячейка говорит, где она. Кнопка, которая на
 * нажатие ничего не делает, - худшее из двух: человек нажимает её трижды,
 * прежде чем решить, что сломано.
 */
export function Pads() {
  const sounds = useSounds();
  const play = usePlaySound();
  const toast = useToast();

  const data = sounds.data;
  const count = data?.pads ?? 12;
  const filled = data?.sounds ?? [];

  /* Ячеек ровно столько, сколько задано: звуки сверх сетки не теряются - они
     видны в библиотеке и встают на сетку, как только она подрастёт. */
  const cells = Array.from({ length: count }, (_, index) => filled[index] ?? null);

  return (
    <div data-right="pads" className="flex h-full min-h-0 flex-col">
      <div
        data-console-scroll="pads"
        tabIndex={0}
        role="region"
        aria-label="Пады"
        /* Сетка начинается сразу под шапкой консоли: отдельная строка
           заголовка над ней - это 30 px, которых у сетки нет. Управление
           звуком живёт в верхней полосе, как у эталона. */
        className="min-h-0 flex-1 overflow-y-auto px-3 pt-2 pb-3"
      >
        {/*
          ОДНО СООБЩЕНИЕ ОБ ОДНОМ, А НЕ ДВА.

          Находка владельца: над пустой сеткой стояла ещё и строка «Звуков
          пока нет» - сетка из двенадцати пронумерованных пустых ячеек и
          текст говорили одно и то же. Осталось то, что быстрее приводит к
          ПЕРВОМУ ЗВУКУ: кнопка в библиотеку. В пустой сетке нажать нечего,
          а первый звук добавляют не здесь.

          Сетка возвращается, как только есть хотя бы один звук: дальше
          пронумерованные ячейки полезны - по ним видно, куда встанет
          следующий.
        */}
        {/* ПОКА ЗВУКИ ЕДУТ, «звуков нет» НЕ ПОКАЗЫВАЕТСЯ: у человека с
            полусотней падов на полсекунды появлялось бы сообщение, что у
            него ничего нет. Во время загрузки рисуется обычная сетка. */}
        {data !== undefined && filled.length === 0 ? (
          /* ПУСТОЕ СОСТОЯНИЕ - ОБЩИЙ ШАБЛОН. Было два вида в одной
             колонке: здесь текстом слева с кнопкой, у чата и гифтеров -
             по центру со значком (находка владельца 2026-09-25). */
          <EmptyState
            id="pads-empty"
            icon="pads"
            title="Звуков пока нет"
            action={
              /* ВТОРИЧНАЯ, а не главная: в правой колонке главное действие
                 одно, и это то, что ведёт к эфиру («Выбрать игру» в
                 состоянии без эфира). Набрать звуки - подготовка, её делают
                 до эфира и не в первую очередь. Правило: одна главная
                 кнопка на зону (`e2e/console-roles.spec.ts`). */
              <Button asChild variant="secondary" size="s">
                <Link to="/app/settings/djpad">Открыть библиотеку звуков</Link>
              </Button>
            }
          >
            Наберите их из нашего набора, своим файлом или по ссылке.
          </EmptyState>
        ) : (
          <div data-control-row="pads-head" className="@container grid grid-cols-4 gap-1">
            {cells.map((sound, index) =>
              sound === null ? (
                <EmptyPad key={`empty-${index}`} index={index} />
              ) : (
                <FilledPad
                  key={sound.id}
                  sound={sound}
                  index={index}
                  onPlay={() => {
                    play.mutate(sound.id, {
                      onError: () => {
                        toast.show({
                          tone: 'warning',
                          title: 'Звук не ушёл в эфир',
                          text: 'Проверьте, открыт ли оверлей: звучит он, а не консоль.',
                        });
                      },
                    });
                  }}
                />
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Заполненная ячейка: цвет свой, номер виден, нажатие уходит в эфир. */
function FilledPad({
  sound,
  index,
  onPlay,
}: {
  sound: SoundRow;
  index: number;
  onPlay: () => void;
}) {
  return (
    /*
     * ПЛИТКА ПАДА - РОЛЬ «ПЛИТКА» (`Card`), а не кнопка.
     *
     * Внутри - полоса цвета и подпись в две строки; у кнопки пресета
     * высота фиксирована, и держалась плитка только на `aspect-square`,
     * который её перебивал. Роль теперь названа честно, приём тот же, что
     * у плитки каталога и плитки игры (единица 17).
     *
     * РАМКА - ТОКЕНОМ ПРЕСЕТА, ЦВЕТ ЗВУКА - ТОЛЬКО ПОЛОСОЙ СВЕРХУ
     * (решение владельца 2026-09-25): оформление органа управления не
     * задаётся содержимым, цвет остаётся меткой набора.
     */
    <Card asChild size="sm" className="relative gap-0 overflow-hidden p-0">
      {/* eslint-disable-next-line no-restricted-syntax -- `asChild` требует
          голого элемента: это тот самый случай, ради которого он у
          компонента есть. Роль остаётся за Card. */}
      <button
        type="button"
        /* `min-w-0` - ЯЧЕЙКА СЕТКИ ДОЛЖНА УМЕТЬ СЖИМАТЬСЯ: у элемента сетки
           `min-width: auto`, и длинное слово растягивало колонку - вместо
           четырёх плиток по 56 px получалась одна на 176. */
        className="flex aspect-square min-h-14 min-w-0 flex-col justify-end p-2 text-center"
        data-pad-filled={sound.id}
        /* Номер остаётся в разметке: он нужен проверкам и чтецу, а глазу на
           занятой плитке - нет. */
        data-pad-index={index + 1}
        aria-label={`Пад ${index + 1}: ${sound.name} — в эфир${index < 9 ? `, клавиша ${String(index + 1)}` : ''}`}
        onClick={onPlay}
        title={
          index < 9
            ? `${sound.name} — в эфир (клавиша ${String(index + 1)})`
            : `${sound.name} — в эфир`
        }
      >
        <span
          aria-hidden="true"
          /* Цветная полоса рисуется ПОВЕРХ плитки по построению - как лупа
             внутри поля поиска. Пометка объявляет это намерением: иначе
             ворота строк считают наложением любую плашку, которая
             окажется под полосой (плашка клавиши и оказалась). */
          data-inside-field
          data-pad-color
          className="absolute inset-x-0 top-0 h-1.5"
          style={{ backgroundColor: sound.color }}
        />
        {/*
          НОМЕР ПАДА ПОКАЗАН КЛАВИШЕЙ, А НЕ ЦИФРОЙ.

          Находка аудита 29: сорок восемь падов были пронумерованы, и номер
          обещал клавишу, которой не существовало. Теперь клавиша есть у
          первых девяти (решение владельца 2026-09-26, п. 6), и показана
          она так, как выглядит клавиша, - `Kbd` пресета. У остальных
          номера в разметке нет вовсе: обещать нечего.
        */}
        {index < 9 && (
          <Kbd
            aria-hidden="true"
            data-pad-key={index + 1}
            /* В ПОТОКЕ, а не абсолютом: абсолютная плашка ложилась на
               подпись пада на узкой колонке - ворота строк управления
               напечатали «„1" и „Глухой" пересеклись». `mb-auto` держит
               её у верхнего края, подпись остаётся у нижнего. */
            /* ПЛАШКА КЛАВИШИ ПОМЕЩАЕТСЯ НЕ ВСЕГДА. Плитка - квадрат со
               стороной от 56 px, и на узкой колонке подпись занимает её
               целиком: плашка налезала то на подпись, то на цветную
               полосу сверху (ворота строк управления напечатали обе
               пары). Плашка стоит В ПОТОКЕ - место под неё отведено, а не
               отнято у подписи, - и ниже 220 px на сетку уходит совсем:
               клавиша остаётся в подсказке и в имени для чтеца. */
            className="mt-1 mb-auto @max-[220px]:hidden"
          >
            {index + 1}
          </Kbd>
        )}
        <PadLabel name={sound.name} />
      </button>
    </Card>
  );
}

/**
 * Подпись плитки: ДВЕ СТРОКИ, КАЖДАЯ СО СВОИМ МНОГОТОЧИЕМ.
 *
 * Две находки владельца подряд про одну подпись. Первая: `break-words` рвал
 * слово посередине - «Аплодисмент/ы 5». Вторая: запрет разрыва её не
 * дочинил - длинное слово просто обрезалось краем плитки, БЕЗ многоточия:
 * «Подтвержден». Обрезанное без многоточия слово читается как другое слово,
 * а не как сокращение.
 *
 * Почему не CSS. `line-clamp` ставит многоточие на границе СТРОК, а не на
 * границе строки по ширине: слово шире плитки он обрезает молча. Ни
 * `overflow-wrap`, ни `text-overflow` на многострочном боксе этого не
 * меняют. Поэтому слова раскладываются по строкам здесь, и каждая строка -
 * обычный однострочный `truncate`, у которого многоточие работает.
 *
 * Плитка узкая по построению: при 48 падах это ~56 px, и целое русское
 * слово в неё не помещается почти никогда. Выбор между «обрезать» и
 * «сломать слово» сделан в пользу обрезки - см. находку 2 единицы 7.
 */
function PadLabel({ name }: { name: string }) {
  const words = name.split(/\s+/).filter(Boolean);
  const lines = words.slice(0, 2);
  const cut = words.length > lines.length;

  return (
    /* `block w-full min-w-0` - иначе подпись НЕ УСЕКАЕТСЯ, а вылезает за
       плитку: у кнопки пресета содержимое - элемент флексбокса с
       `min-width: auto`, и однострочный `truncate` внутри него меряет
       ширину не плитки, а слова. Визуально слово обрезал `overflow-hidden`
       плитки - без многоточия, ровно тот дефект, против которого заведена
       проверка (`e2e/console-sounds.spec.ts`). */
    <span data-pad-label className="text-foreground block w-full min-w-0 text-xs leading-tight">
      {lines.map((word, index) => (
        <span
          key={`${word}-${String(index)}`}
          data-pad-label-line
          /* `truncate` = overflow hidden + nowrap + ellipsis. Именно на
             однострочном боксе многоточие и появляется. */
          className="block truncate [hyphens:none] [overflow-wrap:normal]"
        >
          {index === lines.length - 1 && cut ? `${word}…` : word}
        </span>
      ))}
    </span>
  );
}

/**
 * Пустая ячейка.
 *
 * НЕ КНОПКА: заполняется она не отсюда, и кликабельный вид обещал бы
 * обратное. Но и не молчит - говорит, где заполнять.
 */
function EmptyPad({ index }: { index: number }) {
  return (
    <div
      data-pad-empty={index + 1}
      aria-hidden="true"
      className="border-foreground/25 rounded-lg text-foreground/80 flex aspect-square min-h-14 items-center justify-center border border-dashed text-xs tabular-nums"
    >
      {index + 1}
    </div>
  );
}
